/**
 * Collapsed/expanded state for the Ask ELSA cards, remembered per user.
 *
 * Both cards open by default; a user who collapses one wants it to stay
 * collapsed the next time they open an order, so the choice is stored. It is a
 * per-viewer convenience, nothing more — a read that throws (Safari private
 * mode, storage disabled, quota) must never take the tab down, so every access
 * is guarded and falls back to "expanded".
 */

export type ElsaCardId = 'read' | 'talk';

const STORAGE_KEY = 'elsaCardCollapsed';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const readAll = (storage: StorageLike | null | undefined): Record<string, boolean> => {
  if (!storage) return {};
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'boolean') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
};

/** Whether a card should render collapsed. Default false (open). */
export const isCardCollapsed = (
  storage: StorageLike | null | undefined,
  id: ElsaCardId
): boolean => readAll(storage)[id] === true;

/** Persist a card's collapsed state. Never throws. */
export const setCardCollapsed = (
  storage: StorageLike | null | undefined,
  id: ElsaCardId,
  collapsed: boolean
): void => {
  if (!storage) return;
  try {
    const all = readAll(storage);
    all[id] = collapsed;
    storage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* A preference that cannot be saved is not worth an error to the user. */
  }
};
